import Phaser from 'phaser';
import { SUBJECT_THEMES, WORLD_TILES, randomElement } from '../config/tileIndex';

export interface TilePalette {
  floor: number[];
  floorEdge?: number[];
  floorDecals?: number[];
  wallH: number[];
  wallV: number[];
  wallCracks?: number[];
  torchEvery?: number;
  decalChance?: number;
  crackChance?: number;
  ambientColor?: number;
  haloColor?: number;
}

const DEFAULT_DECALS = [
  WORLD_TILES.BLOOD_A,
  WORLD_TILES.BONES_A,
  WORLD_TILES.COBWEB_A,
  WORLD_TILES.COBWEB_B,
];

const DEFAULT_FLOOR_EDGE = [
  WORLD_TILES.FLOOR_COBBLE_DRAIN,
  WORLD_TILES.FLOOR_COBBLE_E,
];

const DEFAULT_CRACKS = [
  WORLD_TILES.WALL_STONE_H_CRACK,
  WORLD_TILES.WALL_STONE_V_CRACK,
  WORLD_TILES.WALL_CAVE_H_CRACK,
  WORLD_TILES.WALL_CAVE_V_CRACK,
  WORLD_TILES.WALL_CRYPT_H_CRACK,
  WORLD_TILES.WALL_CRYPT_V_CRACK,
  WORLD_TILES.WALL_SEWER_H_CRACK,
  WORLD_TILES.WALL_SEWER_V_CRACK,
];

const DEFAULT_PALETTE: TilePalette = {
  floor: [WORLD_TILES.FLOOR_STONE_A, WORLD_TILES.FLOOR_STONE_B],
  floorEdge: DEFAULT_FLOOR_EDGE,
  floorDecals: DEFAULT_DECALS,
  wallH: [WORLD_TILES.WALL_STONE_H_A, WORLD_TILES.WALL_STONE_H_B, WORLD_TILES.WALL_STONE_H_C],
  wallV: [WORLD_TILES.WALL_STONE_V_A, WORLD_TILES.WALL_STONE_V_B, WORLD_TILES.WALL_STONE_V_C],
  wallCracks: DEFAULT_CRACKS,
  torchEvery: 4,
  decalChance: 0.08,
  crackChance: 0.12,
  ambientColor: 0x101820,
  haloColor: 0xffdd88,
};

/**
 * Get a weighted tile palette based on the subject theme.
 */
export function getPaletteForSubject(subject: string): TilePalette {
  const theme = SUBJECT_THEMES[subject];
  const basePalette: TilePalette = {
    floor: theme?.floor ?? DEFAULT_PALETTE.floor,
    floorEdge: DEFAULT_FLOOR_EDGE,
    floorDecals: DEFAULT_DECALS,
    wallH: theme?.wallH ?? DEFAULT_PALETTE.wallH,
    wallV: theme?.wallV ?? DEFAULT_PALETTE.wallV,
    wallCracks: DEFAULT_CRACKS,
    torchEvery: 4,
    decalChance: 0.1,
    crackChance: 0.15,
    ambientColor: theme?.ambientColor ?? DEFAULT_PALETTE.ambientColor,
    haloColor: theme?.ambientColor ?? DEFAULT_PALETTE.haloColor,
  };

  // Custom Decals per Subject
  switch (subject) {
    case 'Criminal Law':
    case 'Criminal Procedure':
    case 'Torts':
      basePalette.floorDecals = [
        WORLD_TILES.BLOOD_A,
        WORLD_TILES.BLOOD_B,
        WORLD_TILES.BLOOD_C,
        WORLD_TILES.SKULL,
        WORLD_TILES.BONES_A
      ];
      basePalette.decalChance = 0.15; // Messier
      break;

    case 'Real Property':
    case 'Environmental Law':
      basePalette.floorDecals = [
        WORLD_TILES.MUSHROOM_A,
        WORLD_TILES.MUSHROOM_B,
        WORLD_TILES.VINES,
        WORLD_TILES.FLOOR_GRASS_A
      ];
      basePalette.decalChance = 0.2; // Overgrown
      break;

    case 'Trusts And Estates':
    case 'Wills':
      basePalette.floorDecals = [
        WORLD_TILES.GRAVESTONE_A,
        WORLD_TILES.GRAVESTONE_B,
        WORLD_TILES.BONES_A,
        WORLD_TILES.BONES_B,
        WORLD_TILES.SKULL
      ];
      basePalette.decalChance = 0.12;
      break;

    case 'Agency':
    case 'Contracts':
    case 'Corporations':
      basePalette.floorDecals = [
        WORLD_TILES.RUG_A,
        WORLD_TILES.RUG_B
      ];
      basePalette.decalChance = 0.05; // Cleaner
      break;
      
    case 'Evidence':
    case 'Civil Procedure':
       basePalette.floorDecals = [
        WORLD_TILES.BOOKSHELF_B // Books
      ];
      basePalette.decalChance = 0.08;
      break;

    default:
      basePalette.floorDecals = DEFAULT_DECALS;
      break;
  }

  return basePalette;
}

export function pickFloorFrame(
  x: number,
  y: number,
  width: number,
  height: number,
  palette: TilePalette
): number {
  const onEdge = x === 0 || y === 0 || x === width - 1 || y === height - 1;
  if (onEdge && palette.floorEdge?.length) {
    return randomElement(palette.floorEdge);
  }
  return randomElement(palette.floor);
}

export function pickWallFrame(isHorizontal: boolean, palette: TilePalette): number {
  const base = isHorizontal ? palette.wallH : palette.wallV;
  const crack = palette.crackChance && Math.random() < palette.crackChance;
  if (crack && palette.wallCracks?.length) {
    // Choose a crack variant that matches orientation if possible
    const oriented = palette.wallCracks.filter(idx => {
      const hCracks = [WORLD_TILES.WALL_STONE_H_CRACK, WORLD_TILES.WALL_CAVE_H_CRACK, WORLD_TILES.WALL_CRYPT_H_CRACK, WORLD_TILES.WALL_SEWER_H_CRACK] as number[];
      const vCracks = [WORLD_TILES.WALL_STONE_V_CRACK, WORLD_TILES.WALL_CAVE_V_CRACK, WORLD_TILES.WALL_CRYPT_V_CRACK, WORLD_TILES.WALL_SEWER_V_CRACK] as number[];
      const isH = hCracks.includes(idx);
      const isV = vCracks.includes(idx);
      return isHorizontal ? isH : isV;
    });
    if (oriented.length > 0) return randomElement(oriented);
  }
  return randomElement(base);
}

export function shouldPlaceTorch(x: number, y: number, palette: TilePalette): boolean {
  if (!palette.torchEvery) return false;
  return (x + y) % palette.torchEvery === 0;
}

export function shouldPlaceDecal(palette: TilePalette): boolean {
  return !!palette.decalChance && Math.random() < palette.decalChance;
}

export function pickDecalFrame(palette: TilePalette): number {
  const decals = palette.floorDecals?.length ? palette.floorDecals : DEFAULT_DECALS;
  return randomElement(decals);
}

/**
 * Compute a camera zoom that ensures the map fits on screen, clamped to 1.
 */
export function computeCameraZoom(
  mapWidthTiles: number,
  mapHeightTiles: number,
  scaledTile: number,
  viewportWidth: number,
  viewportHeight: number
): number {
  const mapPixelWidth = mapWidthTiles * scaledTile;
  const mapPixelHeight = mapHeightTiles * scaledTile;

  const margin = 0.9; // keep a small border
  const zoomX = (viewportWidth * margin) / mapPixelWidth;
  const zoomY = (viewportHeight * margin) / mapPixelHeight;

  return Math.min(1, zoomX, zoomY);
}

/**
 * Create a simple halo light using a translucent circle.
 */
export function addHalo(
  scene: Phaser.Scene,
  x: number,
  y: number,
  color: number,
  radius: number = 28,
  alpha: number = 0.16
): Phaser.GameObjects.Arc {
  const halo = scene.add.circle(x, y, radius, color, alpha);
  halo.setBlendMode(Phaser.BlendModes.ADD);
  return halo;
}
